// public/js/modules/map/search-control.js
// Address search for the map: a magnifier in the control bar that unfolds into
// a search field with type-ahead results, and flies to the chosen place.
//
// Two rules shaped this:
//  1. It must never interrupt what is already running. Placing a POI or drawing
//     a geofence survives a search - the control only moves the camera, takes
//     no map clicks, and its Escape stops at the input instead of bubbling to
//     the document handler those modes listen on.
//  2. It has to be usable with a thumb. The field REPLACES the base-layer row
//     rather than floating over the map, so it never covers what you searched
//     for and never needs a width the phone does not have.
import { suggestPlaces, resolvePlace, parseCoordinates, geocodingAvailable } from './geocode.js';

const RECENT_KEY = 'map.search.recent.v1';
const MAX_RECENT = 5;
const DEBOUNCE_MS = 220;

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, MAX_RECENT); }
  catch { return []; }
};
const pushRecent = (label) => {
  try {
    const next = [label, ...readRecent().filter(x => x !== label)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* private mode - recents are a convenience, not state */ }
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

/**
 * Build the control. Returns the pieces the map page places into its own bar:
 *   button    - the magnifier, belongs next to the base-layer selector
 *   row       - the search field, hidden until the magnifier is pressed
 *   isOpen()  - so the bar can keep its collapse behaviour in step
 */
export function createMapSearchControl(map) {
  const button = document.createElement('button');
  button.type = 'button';
  // Same frame as every other control button (.map-ctrl-btn = 42x42), or it
  // sits in the bar a size smaller than its neighbours.
  button.className = 'btn btn-sm btn-light shadow-sm d-flex align-items-center '
    + 'justify-content-center map-ctrl-btn map-search-btn';
  button.title = 'Search for an address or place';
  button.setAttribute('aria-label', 'Search for an address or place');
  button.innerHTML = '<i class="fa-solid fa-magnifying-glass"></i>';

  const row = document.createElement('div');
  row.className = 'map-search-row';
  row.hidden = true;
  row.innerHTML = `
    <div class="map-search-field">
      <i class="fa-solid fa-magnifying-glass map-search-field-icon"></i>
      <input type="search" class="form-control form-control-sm map-search-input"
             placeholder="Address, place or 47.37, 8.54" autocomplete="off"
             autocorrect="off" autocapitalize="off" spellcheck="false"
             enterkeyhint="search" aria-label="Address or place">
      <button type="button" class="map-search-close" aria-label="Close search">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <div class="map-search-results" hidden></div>`;

  const input = row.querySelector('.map-search-input');
  const closeBtn = row.querySelector('.map-search-close');
  const results = row.querySelector('.map-search-results');

  let marker = null;        // the pin on the last found place
  let items = [];           // what the list currently shows
  let active = -1;          // keyboard selection
  let timer = null;
  let inflight = null;      // AbortController of the running suggest call

  const near = () => {
    try { const c = map.getCenter(); return [c.lng, c.lat]; } catch { return null; }
  };

  // ---------------------------------------------------------------- results --
  function paint(message = null) {
    if (message) {
      results.innerHTML = `<div class="map-search-msg">${esc(message)}</div>`;
      results.hidden = false;
      return;
    }
    if (!items.length) { results.hidden = true; results.innerHTML = ''; return; }
    results.innerHTML = items.map((it, i) => `
      <button type="button" class="map-search-item${i === active ? ' active' : ''}" data-i="${i}">
        <i class="fa-solid ${it.recent ? 'fa-clock-rotate-left' : it.coords ? 'fa-crosshairs' : 'fa-location-dot'} map-search-item-icon"></i>
        <span class="map-search-item-text">${esc(it.text)}</span>
      </button>`).join('');
    results.hidden = false;
  }

  function showRecent() {
    const recent = readRecent();
    items = recent.map(text => ({ text, recent: true }));
    active = -1;
    if (!items.length) { results.hidden = true; return; }
    paint();
  }

  // ------------------------------------------------------------- searching --
  function schedule() {
    clearTimeout(timer);
    const text = input.value.trim();
    if (inflight) { inflight.abort(); inflight = null; }

    if (!text) { showRecent(); return; }

    // A coordinate pair is answered locally and instantly - no request at all.
    const coords = parseCoordinates(text);
    if (coords) {
      items = [{ text: `${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`, coords }];
      active = 0;
      paint();
      return;
    }
    if (text.length < 3) { results.hidden = true; return; }

    timer = setTimeout(async () => {
      const ctl = new AbortController();
      inflight = ctl;
      try {
        const found = await suggestPlaces(text, { near: near(), signal: ctl.signal });
        if (ctl.signal.aborted) return;
        items = found.map(f => ({ text: f.text, magicKey: f.magicKey }));
        active = -1;
        if (!items.length) paint('Nothing found.'); else paint();
      } catch (e) {
        if (e.name === 'AbortError') return;
        items = [];
        paint(e.message || 'Address search failed.');
      } finally {
        if (inflight === ctl) inflight = null;
      }
    }, DEBOUNCE_MS);
  }

  // ------------------------------------------------------------- the result --
  function dropMarker(place) {
    if (marker) { marker.remove(); marker = null; }
    const el = document.createElement('div');
    el.className = 'map-search-pin';
    el.innerHTML = `<i class="fa-solid fa-location-dot"></i>`;
    el.title = place.label;
    // Click the pin to drop it - it is a find, not something the user owns.
    el.addEventListener('click', (e) => { e.stopPropagation(); clearMarker(); });
    marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([place.lon, place.lat])
      .addTo(map);
  }

  function clearMarker() {
    if (marker) { marker.remove(); marker = null; }
  }

  async function go(item) {
    if (!item) return;
    let place = null;
    if (item.coords) {
      place = { label: item.text, lon: item.coords.lon, lat: item.coords.lat, extent: null };
    } else {
      input.classList.add('is-busy');
      try {
        place = await resolvePlace({ text: item.text, magicKey: item.magicKey }, { near: near() });
      } catch (e) {
        paint(e.message || 'Address search failed.');
        return;
      } finally {
        input.classList.remove('is-busy');
      }
    }
    if (!place) { paint('Nothing found.'); return; }

    pushRecent(item.text);
    dropMarker(place);
    // An extent exists for anything larger than a building - use it instead of
    // guessing a zoom that is wrong for both a city and a house number.
    if (place.extent && Number.isFinite(place.extent.xmin)) {
      map.fitBounds(
        [[place.extent.xmin, place.extent.ymin], [place.extent.xmax, place.extent.ymax]],
        { padding: 60, maxZoom: 17, duration: 800 }
      );
    } else {
      map.flyTo({ center: [place.lon, place.lat], zoom: Math.max(map.getZoom(), 16), duration: 800 });
    }
    close();
  }

  // ------------------------------------------------------------ open/close --
  function open() {
    row.hidden = false;
    button.classList.add('active');
    row.parentElement?.classList.add('search-open');
    input.focus();
    input.select();
    // Reopening with the last query still in the field: show what it finds, not
    // a history list that does not match what is written above it.
    if (input.value.trim()) schedule(); else showRecent();
  }

  function close() {
    clearTimeout(timer);
    if (inflight) { inflight.abort(); inflight = null; }
    row.hidden = true;
    results.hidden = true;
    button.classList.remove('active');
    row.parentElement?.classList.remove('search-open');
    input.blur();
  }

  const isOpen = () => !row.hidden;

  // ------------------------------------------------------------------ wiring --
  button.onclick = () => (isOpen() ? close() : open());
  closeBtn.onclick = () => { clearMarker(); close(); };
  input.addEventListener('input', schedule);
  input.addEventListener('focus', () => { if (!input.value.trim()) showRecent(); });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Stop here. Placing a POI and drawing a geofence both cancel on a
      // document-level Escape, and closing a search box must not cancel them.
      e.stopPropagation();
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      active = e.key === 'ArrowDown'
        ? (active + 1) % items.length
        : (active <= 0 ? items.length - 1 : active - 1);
      paint();
      results.querySelector('.map-search-item.active')?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && items[active]) { go(items[active]); return; }
      const text = input.value.trim();
      if (!text) return;
      const coords = parseCoordinates(text);
      go(coords ? { text, coords } : { text });   // free text: let the geocoder decide
    }
  });

  // Pointerdown, not click: on a phone the keyboard closing on blur moves the
  // list out from under the finger before a click would land.
  results.addEventListener('pointerdown', (e) => {
    const btn = e.target.closest('.map-search-item');
    if (!btn) return;
    e.preventDefault();
    go(items[Number(btn.dataset.i)]);
  });

  return { button, row, isOpen, close, clearMarker, available: geocodingAvailable() };
}
